import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const EMPTY = {
  application_type: "first_time",
  preferred_course_date: "",
  full_name: "",
  date_of_birth: "",
  preferred_language: "portuguese",
  phone: "",
  email: "",
  address: "",
  postal_code: "",
  city: "",
  licence_number: "",
  licence_country: "",
  licence_category: "",
  licence_expiry: "",
  tvde_certificate_number: "",
  certificate_expiry: "",
  agree: false,
};

function Section({ title, children }) {
  return (
    <div className="border border-border rounded-lg p-5 space-y-4 bg-white/40">
      <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{title}</h2>
      {children}
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required = true, placeholder }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm">{label}</Label>
      <Input id={id} data-testid={`apply-${id}`} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} required={required} className="mt-1" />
    </div>
  );
}

export default function Apply() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree) { toast.error("Please accept the declaration"); return; }
    setBusy(true);
    try {
      await api.post("/applications", f);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto px-6 py-24 text-center fade-in" data-testid="apply-success">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600 mb-6" />
          <h1 className="font-serif text-4xl font-medium mb-4">Application received!</h1>
          <p className="text-muted-foreground mb-2">
            Thank you, {f.full_name.split(" ")[0]}. Our team will review your admission and, once approved,
            you will receive your login details by email at <strong>{f.email}</strong>.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Obrigado! Assim que a sua admissao for aprovada, recebera os dados de acesso por email.
          </p>
          <Link to="/"><Button variant="outline" className="rounded-full" data-testid="apply-back-home">Back to home</Button></Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-16 fade-in">
        <h1 className="font-serif text-4xl font-medium mb-2">Student Admission</h1>
        <p className="text-muted-foreground mb-8 text-sm">
          Apply for GoDrive TVDE training. After review, we'll email you your portal login details.
        </p>

        <form onSubmit={submit} className="space-y-6" data-testid="apply-form">
          <Section title="Application Type">
            <RadioGroup value={f.application_type} onValueChange={set("application_type")} className="flex gap-6">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="first_time" id="type-first" data-testid="apply-type-first" />
                <Label htmlFor="type-first" className="font-normal">First-time training</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="renewal" id="type-renewal" data-testid="apply-type-renewal" />
                <Label htmlFor="type-renewal" className="font-normal">Renewal</Label>
              </div>
            </RadioGroup>
            <Field id="preferred_course_date" label="Preferred course date" type="date" required={false}
              value={f.preferred_course_date} onChange={set("preferred_course_date")} />
          </Section>

          <Section title="Student Details">
            <Field id="full_name" label="Full name" value={f.full_name} onChange={set("full_name")} />
            <Field id="date_of_birth" label="Date of birth" type="date" value={f.date_of_birth} onChange={set("date_of_birth")} />
            <div>
              <Label className="text-sm">Preferred language</Label>
              <RadioGroup value={f.preferred_language} onValueChange={set("preferred_language")} className="flex gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="portuguese" id="lang-pt" data-testid="apply-lang-pt" />
                  <Label htmlFor="lang-pt" className="font-normal">Portuguese</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="english" id="lang-en" data-testid="apply-lang-en" />
                  <Label htmlFor="lang-en" className="font-normal">English</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="other" id="lang-other" data-testid="apply-lang-other" />
                  <Label htmlFor="lang-other" className="font-normal">Other</Label>
                </div>
              </RadioGroup>
            </div>
          </Section>

          <Section title="Contact Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="phone" label="Phone / WhatsApp" value={f.phone} onChange={set("phone")} placeholder="+351 ..." />
              <Field id="email" label="Email" type="email" value={f.email} onChange={set("email")} />
            </div>
            <Field id="address" label="Address" value={f.address} onChange={set("address")} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="postal_code" label="Postal code" value={f.postal_code} onChange={set("postal_code")} placeholder="0000-000" />
              <Field id="city" label="City" value={f.city} onChange={set("city")} />
            </div>
          </Section>

          <Section title="Driving Licence">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="licence_number" label="Driving licence number" value={f.licence_number} onChange={set("licence_number")} />
              <Field id="licence_country" label="Country of issue" value={f.licence_country} onChange={set("licence_country")} />
              <Field id="licence_category" label="Licence category" value={f.licence_category} onChange={set("licence_category")} placeholder="B" />
              <Field id="licence_expiry" label="Expiry date" type="date" value={f.licence_expiry} onChange={set("licence_expiry")} />
            </div>
          </Section>

          {f.application_type === "renewal" && (
            <Section title="Renewal Only">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field id="tvde_certificate_number" label="Current TVDE certificate number"
                  value={f.tvde_certificate_number} onChange={set("tvde_certificate_number")} />
                <Field id="certificate_expiry" label="Certificate expiry date" type="date"
                  value={f.certificate_expiry} onChange={set("certificate_expiry")} />
              </div>
            </Section>
          )}

          <Section title="Declaration">
            <p className="text-sm text-muted-foreground">
              I confirm that the information above is correct and allow GoDrive TVDE to use it only for
              course registration, communication and administration.
            </p>
            <div className="flex items-center gap-2">
              <Checkbox id="agree" checked={f.agree} onCheckedChange={(v) => set("agree")(Boolean(v))} data-testid="apply-agree" />
              <Label htmlFor="agree" className="font-normal">I agree</Label>
            </div>
          </Section>

          <Button type="submit" data-testid="apply-submit" disabled={busy}
            className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 h-12 rounded-full text-base">
            {busy ? "Submitting..." : "Submit application"}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Already have an account? <Link to="/login" className="underline text-foreground" data-testid="apply-link-login">Log in</Link>
          </p>
        </form>
      </div>
    </Layout>
  );
}
